// src/components/work_orders/AssignWorkOrderModal.js
// Enhanced modal for assigning a user to a work order with advanced features and debugging
// --- UPDATE: numeric-id safe (String compare), header no longer crashes on numeric ids,
// and a job hint (type · domain · category) so dispatchers pick the right skill set.
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
    ModalCloseButton, Button, VStack, FormControl, FormLabel, Select,
    useColorModeValue, useToast, Spinner, Text, Box, HStack, Avatar,
    Badge, Icon, Input, InputGroup, InputLeftElement, Divider, Collapse,
    IconButton, Tooltip, ScaleFade, Alert, AlertIcon, AlertDescription, AlertTitle,
    Skeleton, Stack, Tag, TagLabel, TagLeftIcon, Fade
} from '@chakra-ui/react';
import {
    MdPerson, MdSearch, MdAssignment, MdWarning, MdCheckCircle,
    MdInfo, MdRefresh, MdBugReport, MdClose, MdFilterList,MdAutoAwesome
} from 'react-icons/md';

// Import config & auth utils
import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';
// --- UPDATE
import { getDomain, getType, inferDomain, inferType } from './workOrderCatalog';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';

const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);
// --- END UPDATE

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
const DEBUG_CONFIG = {
    enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
    logLevel: 'all', // 'info' | 'warn' | 'error' | 'all'
    logToConsole: true,
    logToUI: false, // Set to true to show debug panel in UI
    logNetwork: true,
    logPerformance: true,
};

// Debug logger with color coding
const debugLog = {
    info: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error' || DEBUG_CONFIG.logLevel === 'warn') return;
        console.log(`%c[AssignWO:${context}] ℹ️ ${message}`, 'color: #3182ce', data || '');
    },
    warn: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error') return;
        console.warn(`%c[AssignWO:${context}] ⚠️ ${message}`, 'color: #d69e2e', data || '');
    },
    error: (context, message, error = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        console.error(`%c[AssignWO:${context}] ❌ ${message}`, 'color: #e53e3e', error || '');
        if (error?.stack) console.error(error.stack);
    },
    network: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logNetwork) return;
        console.log(`%c[AssignWO:${context}] 🌐 ${message}`, 'color: #805ad5', data || '');
    },
    performance: (context, message, duration) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logPerformance) return;
        const color = duration < 100 ? '#38a169' : duration < 500 ? '#d69e2e' : '#e53e3e';
        console.log(`%c[AssignWO:${context}] ⚡ ${message} (${duration}ms)`, `color: ${color}`);
    },
    render: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        console.log(`%c[AssignWO:${context}] 🎨 ${message}`, 'color: #38b2ac', data || '');
    },
};

// Performance monitoring
class PerformanceMonitor {
    constructor(operation) {
        this.operation = operation;
        this.startTime = performance.now();
        this.startMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
    }

    end() {
        const duration = (performance.now() - this.startTime).toFixed(2);
        const memoryDelta = performance.memory 
            ? ((performance.memory.usedJSHeapSize - this.startMemory) / 1048576).toFixed(2)
            : 'N/A';
        
        debugLog.performance(this.operation, 'Operation completed', duration);
        if (memoryDelta !== 'N/A') {
            debugLog.info(this.operation, `Memory delta: ${memoryDelta} MB`);
        }
        
        return { duration, memory: memoryDelta };
    }
}

// ============================================================================
// API CALLS
// ============================================================================
const API_BASE_URL = config.apiUrl;

const handleApiError = async (response, resourceName = 'resource') => {
    let errorMsg = `HTTP error with ${resourceName}! Status: ${response.status}`;
    try {
        const errData = await response.json();
        errorMsg = errData.message || (errData.errors ? errData.errors.map(er => er.msg).join(', ') : errorMsg);
    } catch (e) {
        debugLog.error('handleApiError', 'Failed to parse error response', e);
    }
    throw new Error(errorMsg);
};

const fetchUsers = async (token, roles = ['staff', 'tech']) => {
    const monitor = new PerformanceMonitor('fetchUsers');
    const roleQuery = roles.map(r => `role=${r}`).join('&');
    const url = `${API_BASE_URL}/users?${roleQuery}`;
    
    debugLog.network('fetchUsers', `Fetching users from: ${url}`);
    
    const response = await fetch(url, { 
        headers: { Authorization: `Bearer ${token}` } 
    });
    
    if (!response.ok) {
        debugLog.error('fetchUsers', `Failed to fetch users: ${response.status}`);
        await handleApiError(response, 'users');
    }
    
    const data = await response.json();
    const users = Array.isArray(data) ? data : (data?.data || []);
    
    monitor.end();
    debugLog.info('fetchUsers', `Fetched ${users.length} users`, { roles, count: users.length });
    
    return users;
};

const assignUserToWorkOrder = async (workOrderId, userId, token) => {
    const monitor = new PerformanceMonitor('assignUserToWorkOrder');
    
    debugLog.network('assignUserToWorkOrder', `Assigning user ${userId || 'NULL'} to WO ${workOrderId}`);
    
    const response = await fetch(`${API_BASE_URL}/workorders/${workOrderId}/assign`, {
        method: 'PUT',
        headers: { 
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ userId: userId || null })
    });
    
    if (!response.ok) {
        debugLog.error('assignUserToWorkOrder', `Assignment failed: ${response.status}`);
        await handleApiError(response, 'assigning work order');
    }
    
    const result = await response.json();
    monitor.end();
    debugLog.info('assignUserToWorkOrder', 'Assignment successful', result);
    
    return result;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function AssignWorkOrderModal({ isOpen, onClose, onSuccess, workOrder }) {
    // State
    const [usersForAssignment, setUsersForAssignment] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showDebugPanel, setShowDebugPanel] = useState(DEBUG_CONFIG.logToUI);
    const [showFilters, setShowFilters] = useState(false);
    const [debugInfo, setDebugInfo] = useState({
        lastFetchTime: null,
        userCount: 0,
        filteredCount: 0,
        selectedUser: null,
    });

    const toast = useToast();

    // Debug: Component lifecycle
    useEffect(() => {
        debugLog.render('useEffect:mount', 'Component mounted');
        return () => debugLog.render('useEffect:unmount', 'Component unmounted');
    }, []);

    useEffect(() => {
        debugLog.render('useEffect:isOpen', `Modal ${isOpen ? 'opened' : 'closed'}`, { 
            workOrderId: workOrder?.id,
            assignedUserId: workOrder?.assigned_user_id 
        });
    }, [isOpen, workOrder]);

    // Fetch users when modal opens
    useEffect(() => {
        if (isOpen && workOrder) {
            const currentAssignee = workOrder.assigned_user_id != null ? String(workOrder.assigned_user_id) : ''; // --- UPDATE
            setSelectedUserId(currentAssignee);
            
            debugLog.info('useEffect:fetchUsers', 'Initiating user fetch', {
                workOrderId: workOrder.id,
                currentAssignee
            });

            const fetchAssignableUsers = async () => {
                const monitor = new PerformanceMonitor('fetchAssignableUsers');
                setIsLoadingUsers(true);
                const token = getAuthToken();
                
                if (!token) {
                    debugLog.error('fetchAssignableUsers', 'No auth token found');
                    toast({ 
                        title: "Auth Error", 
                        description: "Authentication token missing",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                    });
                    setIsLoadingUsers(false);
                    return;
                }
                
                try {
                    const data = await fetchUsers(token, ['staff', 'tech']);
                    setUsersForAssignment(data || []);
                    setFilteredUsers(data || []);
                    
                    setDebugInfo(prev => ({
                        ...prev,
                        lastFetchTime: new Date().toISOString(),
                        userCount: data?.length || 0,
                        filteredCount: data?.length || 0,
                    }));
                    
                    monitor.end();
                } catch (err) {
                    debugLog.error('fetchAssignableUsers', 'Failed to fetch users', err);
                    toast({ 
                        title: 'Error fetching users', 
                        description: err.message, 
                        status: 'error',
                        duration: 7000,
                        isClosable: true,
                    });
                    setUsersForAssignment([]);
                    setFilteredUsers([]);
                } finally {
                    setIsLoadingUsers(false);
                }
            };
            
            fetchAssignableUsers();
        } else {
            // Reset when closed or no work order
            debugLog.info('useEffect:reset', 'Resetting component state');
            setUsersForAssignment([]);
            setFilteredUsers([]);
            setSelectedUserId('');
            setSearchQuery('');
            setRoleFilter('all');
            setShowFilters(false);
        }
    }, [isOpen, workOrder, toast]);

    // Filter users based on search and role filter
    useEffect(() => {
        const monitor = new PerformanceMonitor('filterUsers');
        
        let filtered = [...usersForAssignment];
        
        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(user => 
                user.username?.toLowerCase().includes(query) ||
                user.email?.toLowerCase().includes(query) ||
                user.role?.toLowerCase().includes(query)
            );
            debugLog.info('filterUsers', `Search filter applied: "${searchQuery}"`, { 
                resultCount: filtered.length 
            });
        }
        
        // Apply role filter
        if (roleFilter !== 'all') {
            filtered = filtered.filter(user => user.role === roleFilter);
            debugLog.info('filterUsers', `Role filter applied: "${roleFilter}"`, { 
                resultCount: filtered.length 
            });
        }
        
        setFilteredUsers(filtered);
        setDebugInfo(prev => ({ ...prev, filteredCount: filtered.length }));
        
        monitor.end();
    }, [searchQuery, roleFilter, usersForAssignment]);

    // Update debug info when user is selected
    useEffect(() => {
        if (selectedUserId) {
            const user = usersForAssignment.find(u => sameId(u.id, selectedUserId)); // --- UPDATE
            setDebugInfo(prev => ({ ...prev, selectedUser: user || null }));
            debugLog.info('useEffect:selection', 'User selected', { 
                userId: selectedUserId,
                username: user?.username 
            });
        } else {
            setDebugInfo(prev => ({ ...prev, selectedUser: null }));
        }
    }, [selectedUserId, usersForAssignment]);

    // Helper to identify the AI suggestion (NEW)
    const aiSuggestedUserId = workOrder?.suggested_user_id;
    const aiConfidence = workOrder?.assignment_confidence_score;

    // Get currently assigned user
    const currentlyAssignedUser = useMemo(() => {
        return usersForAssignment.find(u => sameId(u.id, workOrder?.assigned_user_id)); // --- UPDATE
    }, [usersForAssignment, workOrder?.assigned_user_id]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const monitor = new PerformanceMonitor('handleSubmit');
        
        if (!workOrder?.id) {
            debugLog.error('handleSubmit', 'Work Order ID missing');
            toast({ 
                title: "Error", 
                description: "Work Order ID missing.", 
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        
        debugLog.info('handleSubmit', 'Starting assignment process', {
            workOrderId: workOrder.id,
            selectedUserId,
            previousUserId: workOrder.assigned_user_id,
        });
        
        setIsSubmitting(true);
        const token = getAuthToken();
        
        if (!token) {
            debugLog.error('handleSubmit', 'Auth token missing');
            toast({ 
                title: "Auth Error",
                description: "Authentication token missing",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const userIdToAssign = selectedUserId || null;
            await assignUserToWorkOrder(workOrder.id, userIdToAssign, token);
            
            const successMessage = userIdToAssign ? "Work Order Assigned" : "Work Order Unassigned";
            debugLog.info('handleSubmit', successMessage, {
                workOrderId: workOrder.id,
                assignedTo: userIdToAssign,
            });
            
            toast({ 
                title: successMessage,
                description: userIdToAssign 
                    ? `Assigned to ${debugInfo.selectedUser?.username || 'user'}`
                    : 'Work order is now unassigned',
                status: "success",
                duration: 4000,
                isClosable: true,
            });
            
            // --- UPDATE
            publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { action: userIdToAssign ? 'assigned' : 'unassigned', id: workOrder.id, asset_id: workOrder.asset_id ?? null });
            // --- END UPDATE
            monitor.end();
            onSuccess();
            onClose();
        } catch (err) {
            debugLog.error('handleSubmit', 'Assignment failed', err);
            toast({ 
                title: "Assignment Failed", 
                description: err.message, 
                status: "error", 
                duration: 7000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle refresh users
    const handleRefreshUsers = useCallback(async () => {
        debugLog.info('handleRefreshUsers', 'Manually refreshing user list');
        
        setIsLoadingUsers(true);
        const token = getAuthToken();
        
        if (!token) {
            toast({ title: "Auth Error", status: "error" });
            setIsLoadingUsers(false);
            return;
        }
        
        try {
            const data = await fetchUsers(token, ['staff', 'tech']);
            setUsersForAssignment(data || []);
            setFilteredUsers(data || []);
            
            toast({
                title: "Users Refreshed",
                description: `Loaded ${data?.length || 0} users`,
                status: "success",
                duration: 3000,
            });
        } catch (err) {
            debugLog.error('handleRefreshUsers', 'Refresh failed', err);
            toast({ 
                title: 'Refresh Failed', 
                description: err.message, 
                status: 'error' 
            });
        } finally {
            setIsLoadingUsers(false);
        }
    }, [toast]);

    // Colors
    const formLabelColor = useColorModeValue("gray.700", "gray.200");
    const modalBg = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("gray.50", "gray.700");
    const mutedTextColor = useColorModeValue("gray.500", "gray.400");
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const debugPanelBg = useColorModeValue("yellow.50", "yellow.900");
    const debugBorderColor = useColorModeValue("yellow.200", "yellow.700");

    // --- FIX: Define the hook at the top level, NOT inside the JSX condition ---
    const aiBannerBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.16)');
    // --- UPDATE: job hint
    const jobTypeKey = inferType(workOrder || {});
    const jobType = getType(jobTypeKey);
    const jobDomain = getDomain(inferDomain(workOrder || {}));
    // --- END UPDATE
    // --------------------------------------------------------------------------

    // User count badge color
    const getUserCountColor = () => {
        if (filteredUsers.length === 0) return 'red';
        if (filteredUsers.length < 5) return 'orange';
        return 'green';
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            isCentered 
            size="xl"
            scrollBehavior="inside"
        >
            <ModalOverlay backdropFilter="blur(4px)" />
            <ScaleFade in={isOpen} initialScale={0.95}>
                <ModalContent 
                    as="form" 
                    onSubmit={handleSubmit}
                    bg={modalBg}
                    borderRadius="xl"
                    boxShadow="2xl"
                >
                    <ModalHeader>
                        <HStack spacing={3}>
                            <Icon as={MdAssignment} boxSize={6} color="teal.500" />
                            <VStack align="start" spacing={0}>
                                <Text fontSize="lg" fontWeight="bold">
                                    Assign Work Order
                                </Text>
                                <Text fontSize="sm" fontWeight="normal" color={mutedTextColor}>
                                    ID: {String(workOrder?.id ?? '').substring(0, 8)}...{/* --- UPDATE: numeric-id safe */}
                                </Text>
                            </VStack>
                        </HStack>
                    </ModalHeader>
                    
                    <ModalCloseButton />
                    
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">
                            {/* --- UPDATE START: AI SUGGESTION BANNER --- */}
                            {aiSuggestedUserId && !workOrder.assigned_user_id && (
                                <Alert 
                                    status="success" 
                                    variant="subtle" 
                                    borderRadius="md" 
                                    cursor="pointer" 
                                    onClick={() => setSelectedUserId(String(aiSuggestedUserId))}
                                    borderWidth="1px"
                                    borderColor="purple.200"
                                    bg={aiBannerBg}
                                >
                                    <Icon as={MdAutoAwesome} boxSize={5} mr={3} color="purple.500" />
                                    <Box flex="1">
                                        <AlertTitle fontSize="sm" mb={1}>AI Recommendation Available</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            The system is <strong>{aiConfidence || 'high'}%</strong> confident that this is the best match.
                                            <br />
                                            <Text as="span" fontWeight="bold" color="purple.600">Click here to auto-select.</Text>
                                        </AlertDescription>
                                    </Box>
                                    {/* Resolve the username from the list if available */}
                                    <Tag colorScheme="purple" size="sm" variant="solid">
                                        <TagLeftIcon as={MdAutoAwesome} />
                                        <TagLabel>
                                            {usersForAssignment.find(u => sameId(u.id, aiSuggestedUserId))?.username || 'Suggested Tech'}
                                        </TagLabel>
                                    </Tag>
                                </Alert>
                            )}
                            {/* --- UPDATE END --- */}
                            {/* --- UPDATE: what kind of job this is */}
                            {(jobType || jobDomain) && (
                                <HStack spacing={2} flexWrap="wrap" fontSize="xs" color={mutedTextColor}>
                                    <Text>Job:</Text>
                                    {jobType && <Badge colorScheme={jobType.color}>{jobType.short}</Badge>}
                                    {jobDomain && <Badge colorScheme={jobDomain.color} variant="outline">{jobDomain.short}</Badge>}
                                    {workOrder?.category && <Badge variant="subtle">{workOrder.category}</Badge>}
                                    <Text>— pick someone with {jobDomain ? jobDomain.short.toLowerCase() : 'the right'} skills{jobTypeKey === 'inspection' ? ' who can record findings' : ''}.</Text>
                                </HStack>
                            )}
                            {/* --- END UPDATE */}
                            {/* Current Assignment Info */}
                            {currentlyAssignedUser && (
                                <Fade in={true}>
                                    <Alert status="info" borderRadius="md" variant="left-accent">
                                        <AlertIcon />
                                        <AlertDescription fontSize="sm">
                                            Currently assigned to{' '}
                                            <Text as="span" fontWeight="bold">
                                                {currentlyAssignedUser.username}
                                            </Text>
                                            {' '}({currentlyAssignedUser.role})
                                        </AlertDescription>
                                    </Alert>
                                </Fade>
                            )}

                            {/* Search and Filter Section */}
                            <Box>
                                <HStack spacing={2} mb={2}>
                                    <InputGroup size="md">
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={MdSearch} color={mutedTextColor} />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search users by name, email, or role..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            borderRadius="md"
                                        />
                                    </InputGroup>
                                    <Tooltip label="Toggle Filters">
                                        <IconButton
                                            icon={<MdFilterList />}
                                            onClick={() => setShowFilters(!showFilters)}
                                            variant={showFilters ? "solid" : "outline"}
                                            colorScheme={showFilters ? "teal" : "gray"}
                                            aria-label="Toggle filters"
                                        />
                                    </Tooltip>
                                    <Tooltip label="Refresh Users">
                                        <IconButton
                                            icon={<MdRefresh />}
                                            onClick={handleRefreshUsers}
                                            isLoading={isLoadingUsers}
                                            variant="outline"
                                            colorScheme="blue"
                                            aria-label="Refresh users"
                                        />
                                    </Tooltip>
                                    {DEBUG_CONFIG.enabled && (
                                        <Tooltip label="Toggle Debug Panel">
                                            <IconButton
                                                icon={<MdBugReport />}
                                                onClick={() => setShowDebugPanel(!showDebugPanel)}
                                                variant={showDebugPanel ? "solid" : "outline"}
                                                colorScheme="yellow"
                                                size="sm"
                                                aria-label="Toggle debug"
                                            />
                                        </Tooltip>
                                    )}
                                </HStack>

                                {/* Filter Options */}
                                <Collapse in={showFilters} animateOpacity>
                                    <Box 
                                        mt={3} 
                                        p={3} 
                                        bg={cardBg} 
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <HStack spacing={2} wrap="wrap">
                                            <Text fontSize="sm" fontWeight="medium">
                                                Filter by role:
                                            </Text>
                                            <Tag
                                                size="md"
                                                variant={roleFilter === 'all' ? 'solid' : 'outline'}
                                                colorScheme="teal"
                                                cursor="pointer"
                                                onClick={() => setRoleFilter('all')}
                                                transition="all 0.2s"
                                                _hover={{ transform: 'scale(1.05)' }}
                                            >
                                                <TagLabel>All</TagLabel>
                                            </Tag>
                                            <Tag
                                                size="md"
                                                variant={roleFilter === 'staff' ? 'solid' : 'outline'}
                                                colorScheme="blue"
                                                cursor="pointer"
                                                onClick={() => setRoleFilter('staff')}
                                                transition="all 0.2s"
                                                _hover={{ transform: 'scale(1.05)' }}
                                            >
                                                <TagLabel>Staff</TagLabel>
                                            </Tag>
                                            <Tag
                                                size="md"
                                                variant={roleFilter === 'tech' ? 'solid' : 'outline'}
                                                colorScheme="purple"
                                                cursor="pointer"
                                                onClick={() => setRoleFilter('tech')}
                                                transition="all 0.2s"
                                                _hover={{ transform: 'scale(1.05)' }}
                                            >
                                                <TagLabel>Tech</TagLabel>
                                            </Tag>
                                        </HStack>
                                    </Box>
                                </Collapse>
                            </Box>

                            <Divider />

                            {/* User Selection */}
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" color={formLabelColor}>
                                    <HStack spacing={2}>
                                        <Icon as={MdPerson} />
                                        <Text>Select User</Text>
                                        <Badge colorScheme={getUserCountColor()} borderRadius="full">
                                            {filteredUsers.length} available
                                        </Badge>
                                    </HStack>
                                </FormLabel>
                                
                                {isLoadingUsers ? (
                                    <Stack spacing={2}>
                                        <Skeleton height="40px" borderRadius="md" />
                                        <Text fontSize="xs" color={mutedTextColor} textAlign="center">
                                            Loading users...
                                        </Text>
                                    </Stack>
                                ) : (
                                    <Select
                                        placeholder="-- Select User --"
                                        value={selectedUserId}
                                        onChange={(e) => setSelectedUserId(e.target.value)}
                                        name="assigned_user_id"
                                        size="lg"
                                        borderRadius="md"
                                        focusBorderColor="teal.500"
                                    >
                                        {/* Option to unassign */}
                                        <option value="">🚫 Unassign (No user)</option>
                                        
                                        {/* --- UPDATE: Highlighting Recommended User in Dropdown --- */}
                                        {filteredUsers.length > 0 ? (
                                            filteredUsers.map(user => {
                                                const isSuggested = sameId(user.id, aiSuggestedUserId); // --- UPDATE
                                                return (
                                                    <option 
                                                        key={user.id} 
                                                        value={user.id}
                                                        style={isSuggested ? { fontWeight: 'bold', color: '#6B46C1' } : {}}
                                                    >
                                                        {isSuggested ? '✨ ' : ''}
                                                        {user.username} {isSuggested ? '(AI Recommended)' : `(${user.role})`}
                                                        {user.email ? ` - ${user.email}` : ''}
                                                    </option>
                                                );
                                            })
                                        ) : (
                                            <option disabled>No users match your filters</option>
                                        )}
                                        {/* --- UPDATE END --- */}
                                    </Select>
                                )}

                                {/* Selected User Preview */}
                                {selectedUserId && debugInfo.selectedUser && (
                                    <Fade in={true}>
                                        <Box 
                                            mt={3} 
                                            p={3} 
                                            bg={cardBg} 
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <HStack spacing={3}>
                                                <Avatar 
                                                    size="sm" 
                                                    name={debugInfo.selectedUser.username}
                                                    bg="teal.500"
                                                />
                                                <VStack align="start" spacing={0} flex={1}>
                                                    <Text fontSize="sm" fontWeight="bold">
                                                        {debugInfo.selectedUser.username}
                                                    </Text>
                                                    <HStack spacing={2}>
                                                        <Badge colorScheme="purple" fontSize="xs">
                                                            {debugInfo.selectedUser.role}
                                                        </Badge>
                                                        {debugInfo.selectedUser.email && (
                                                            <Text fontSize="xs" color={mutedTextColor}>
                                                                {debugInfo.selectedUser.email}
                                                            </Text>
                                                        )}
                                                    </HStack>
                                                </VStack>
                                                <Icon as={MdCheckCircle} color="green.500" boxSize={5} />
                                            </HStack>
                                        </Box>
                                    </Fade>
                                )}
                            </FormControl>

                            {/* Warning for unassignment */}
                            {!selectedUserId && (
                                <Fade in={true}>
                                    <Alert status="warning" borderRadius="md" variant="left-accent">
                                        <AlertIcon />
                                        <AlertDescription fontSize="sm">
                                            No user selected. This will unassign the work order.
                                        </AlertDescription>
                                    </Alert>
                                </Fade>
                            )}

                            {/* Debug Panel */}
                            {DEBUG_CONFIG.enabled && showDebugPanel && (
                                <Collapse in={showDebugPanel} animateOpacity>
                                    <Box
                                        p={3}
                                        bg={debugPanelBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={debugBorderColor}
                                        fontSize="xs"
                                        fontFamily="mono"
                                    >
                                        <HStack justify="space-between" mb={2}>
                                            <HStack>
                                                <Icon as={MdBugReport} />
                                                <Text fontWeight="bold">Debug Panel</Text>
                                            </HStack>
                                            <IconButton
                                                icon={<MdClose />}
                                                size="xs"
                                                onClick={() => setShowDebugPanel(false)}
                                                aria-label="Close debug panel"
                                            />
                                        </HStack>
                                        <VStack align="stretch" spacing={1}>
                                            <Text><strong>Work Order ID:</strong> {workOrder?.id}</Text>
                                            <Text><strong>Total Users:</strong> {debugInfo.userCount}</Text>
                                            <Text><strong>Filtered Users:</strong> {debugInfo.filteredCount}</Text>
                                            <Text><strong>Selected User ID:</strong> {selectedUserId || 'None'}</Text>
                                            <Text><strong>Current Assignee:</strong> {workOrder?.assigned_user_id || 'Unassigned'}</Text>
                                            <Text><strong>Search Query:</strong> "{searchQuery}"</Text>
                                            <Text><strong>Role Filter:</strong> {roleFilter}</Text>
                                            <Text><strong>Loading:</strong> {isLoadingUsers ? 'Yes' : 'No'}</Text>
                                            <Text><strong>Submitting:</strong> {isSubmitting ? 'Yes' : 'No'}</Text>
                                            <Text><strong>Last Fetch:</strong> {debugInfo.lastFetchTime || 'N/A'}</Text>
                                        </VStack>
                                    </Box>
                                </Collapse>
                            )}
                        </VStack>
                    </ModalBody>
                    
                    <ModalFooter>
                        <Button 
                            type="submit" 
                            colorScheme='teal' 
                            mr={3} 
                            isLoading={isSubmitting || isLoadingUsers}
                            loadingText={isSubmitting ? "Assigning..." : "Loading..."}
                            leftIcon={<MdAssignment />}
                            size="md"
                        >
                            {selectedUserId ? 'Assign User' : 'Unassign'}
                        </Button>
                        <Button 
                            variant='ghost' 
                            onClick={onClose}
                            isDisabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </ScaleFade>
        </Modal>
    );
}

AssignWorkOrderModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    workOrder: PropTypes.object,
};

export default AssignWorkOrderModal;